import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import * as Types from '../shared/types';
import { InitialFilterData, DisableContent, hasFilterOperandValue, serializeFilterOperandValue } from '../shared/constant';
import { convertToFilterCondition, migrateClusterFilterConditions, normalizeConceptDataFilter, transformTestToFilters } from '../shared/appUtil';
import { SharedDataService } from './shared-data.service';
import { ApiCacheService } from './api-cache.service';

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  private readonly sharedDataService = inject(SharedDataService);
  private readonly apiCacheService = inject(ApiCacheService);

  private _allFilter$: BehaviorSubject<Types.DataFilters> = new BehaviorSubject({} as Types.DataFilters);
  private _refreshData$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _applyButtonEnabled$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _filterData$: BehaviorSubject<Types.FilterCondition[]> = new BehaviorSubject([
    { ...InitialFilterData } as Types.FilterCondition,
  ]);
  private _tempFilterData$: BehaviorSubject<Types.FilterCondition[]> = new BehaviorSubject([
    { ...InitialFilterData } as Types.FilterCondition,
  ]);
  private _savedFilterLabel$: BehaviorSubject<string> = new BehaviorSubject('');
  private _filterApplied$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  protected _savedFilterList = signal<Array<Types.FilterListResponse>>([]);
  private _saveButtonEnabled = signal<boolean>(false);
  private _filterDataDisplayed = signal<boolean>(false);
  private _customerEndpointAddressSelected = signal<boolean>(false);

  constructor() {
    // Initialize filter applied status on service creation
    this.updateFilterAppliedStatus();
  }

  setCustomerEndpointAddressSelected = (data: boolean) => {
    this._customerEndpointAddressSelected.set(data);
  };

  public get customerEndpointAddressSelected(): WritableSignal<boolean> {
    return this._customerEndpointAddressSelected;
  }

  setFilterApplied = (data: boolean) => {
    this._filterApplied$.next(data);
  };

  getFilterAppliedStatus = () => {
    return this._filterApplied$;
  };

  getFilterAppliedStatusValue = () => {
    return this._filterApplied$.value;
  };

  public get filterDataDisplayedStatus(): WritableSignal<boolean> {
    return this._filterDataDisplayed;
  }

  setFilterDataDisplayedStatus(enabled: boolean): void {
    this._filterDataDisplayed.set(enabled);
  }

  public get saveButtonEnabled(): WritableSignal<boolean> {
    return this._saveButtonEnabled;
  }

  setSavedButtonEnabled(enabled: boolean): void {
    this._saveButtonEnabled.set(enabled);
  }

  savedFilterListState(): Types.FilterListResponse[] {
    return this._savedFilterList();
  }

  public get savedFilterList(): WritableSignal<Array<Types.FilterListResponse>> {
    return this._savedFilterList;
  }

  setSavedFilterList(filterList: Array<Types.FilterListResponse>): void {
    this._savedFilterList.set(filterList);
  }

  updateSavedFilterList(obj: Types.FilterListResponse, update = false): void {
    const updatedList = this._savedFilterList().map((item) => {
      if (update && item.tag === obj.tag) {
        return obj;
      }
      return item;
    });

    if (!update) {
      updatedList.push(obj);
    }

    this.setSavedFilterList(updatedList);
  }

  removeFilterFromList(tag: string): void {
    const updatedList = this._savedFilterList().filter((item) => item.tag !== tag);
    this.setSavedFilterList(updatedList);
    this.setSavedFilterLabel('');
    this.clearFilters();
    this.updateFilterAppliedStatus();
    if (this._filterDataDisplayed()) {
      this.setRefreshDataStatus(true);
      this.setFilterDataDisplayedStatus(false);
    }
  }

  updateFilterData = (data: Types.FilterCondition[]) => {
    const migrated = migrateClusterFilterConditions(data);
    this._filterData$.next(migrated);
    this._tempFilterData$.next(this.deepCloneFilterConditions(migrated));
    this.updateFilterAppliedStatus();
    this.updateApplyButtonState();
  };

  setSavedFilterLabel = (data: string) => {
    this._savedFilterLabel$.next(data);
  };

  getSavedFilterLabel = () => {
    return this._savedFilterLabel$;
  };

  setAllFilters = (data: Types.DataFilters) => {
    this._allFilter$.next(data);
  };

  updateAllFilters = (data: Types.DataFilters) => {
    this._allFilter$.next({
      filters: {
        ...this._allFilter$.value.filters,
        ...data.filters,
      },
    });
  };

  getAllFilters = () => {
    return this._allFilter$;
  };

  getAllFiltersCurrentValue = () => {
    return this._allFilter$.value;
  };

  setRefreshDataStatus = (data: boolean) => {
    this._refreshData$.next(data);
  };

  getRefreshDataStatus = () => {
    return this._refreshData$;
  };

  getRefreshDataStatusValue = () => {
    return this._refreshData$.value;
  };

  setApplyButtonStatus = (data: boolean) => {
    this._applyButtonEnabled$.next(data);
  };

  getApplyButtonStatus = () => {
    return this._applyButtonEnabled$;
  };

  /**
   * Enables Apply Changes when the current UI state (temp) differs from the applied filter state.
   * This includes when the user clears a filter value or removes conditions — they can then apply
   * to fetch data without those filters.
   */
  updateApplyButtonState = () => {
    const appliedPayload = transformTestToFilters(this._filterData$.value).data;
    const tempPayload = transformTestToFilters(this._tempFilterData$.value).data;
    const hasChanged =
      appliedPayload.length !== tempPayload.length ||
      JSON.stringify(appliedPayload) !== JSON.stringify(tempPayload);
    this._applyButtonEnabled$.next(hasChanged);
  };

  setFilterCondition = (data: Types.FilterCondition[]) => {
    this._filterData$.next([...data]);
    this._tempFilterData$.next([...data]);
    this.updateFilterAppliedStatus();
  };

  getFilterCondition = () => {
    return this._filterData$;
  };

  getTempFilterCondition = () => {
    return this._tempFilterData$;
  };

  getPrimaryTopics = () => {
    return this._filterData$;
  };

  getFilterConditionDataValue = () => {
    return this._filterData$.value;
  };

  // Methods for temporary filter data management
  getTempFilterConditionDataValue = () => {
    return this._tempFilterData$.value;
  };

  setTempFilterCondition = (data: Types.FilterCondition[]) => {
    this._tempFilterData$.next([...data]);
  };

  /**
   * Append concept filters into the existing TEMP filter list (does not apply).
   * - First injected filter ANDs with prior conditions when any exist; otherwise operator is null ("Where").
   * - Subsequent concept filters use combineWith (default OR).
   */
  injectConceptFilters = (
    conceptFilters: Types.ConceptDataFilter[] | Types.FilterRequest[],
    combineWith: 'OR' | 'AND' = 'OR'
  ) => {
    const normalized = conceptFilters
      .map((f) => normalizeConceptDataFilter(f))
      .filter((f) => f.columnName && f.operationType);

    if (!normalized.length) {
      return;
    }

    const current = this._tempFilterData$.value;
    const existingValid = current.filter((c) => c.selectedColumn?.value);
    const hasExisting = existingValid.length > 0;
    const conceptConditions = convertToFilterCondition(normalized).map((condition, i) => ({
      ...condition,
      operator: i === 0 ? (hasExisting ? 'AND' : null) : combineWith,
    }));

    const merged = [...existingValid, ...conceptConditions].map((item, index) => ({
      ...item,
      index: index + 1,
      operator: index === 0 ? null : item.operator,
    }));

    this._tempFilterData$.next(merged);
    this.setSavedButtonEnabled(true);
    this.updateApplyButtonState();
  };

  applyTempFiltersToActual = () => {
    this._filterData$.next([...this._tempFilterData$.value]);
    // Check if there are any valid filters applied and update the applied status
    this.updateFilterAppliedStatus();
    // Clear cache when filters are applied to ensure fresh data
    this.clearCacheOnFilterChange();
  };

  /**
   * Deep-clones applied filter conditions so temp state does not share references with applied state.
   * Keeps applied filters intact when the filter section is closed and reopened.
   */
  private deepCloneFilterConditions(conditions: Types.FilterCondition[]): Types.FilterCondition[] {
    return conditions.map((c) => ({
      ...c,
      selectedColumn: c.selectedColumn ? { ...c.selectedColumn } : undefined,
      selectedOperation: c.selectedOperation ? { ...c.selectedOperation } : undefined,
      value: Array.isArray(c.value) ? [...c.value] : c.value,
      clusterData: (c.clusterData ?? []).map((cluster) => ({
        ...cluster,
        selectedCluster: cluster.selectedCluster ? { ...cluster.selectedCluster } : undefined,
        selectedOperation: cluster.selectedOperation ? { ...cluster.selectedOperation } : undefined,
        value: Array.isArray(cluster.value) ? [...cluster.value] : cluster.value,
      })),
    }));
  }

  resetTempFiltersToActual = () => {
    this._tempFilterData$.next(this.deepCloneFilterConditions(this._filterData$.value));
    this.updateApplyButtonState();
  };

  updateFilterAppliedStatus = () => {
    const appliedFilters = this._filterData$.value;
    const hasValidFilters = appliedFilters.some((filter) => {
      // Require selected column for all filters
      if (!filter.selectedColumn?.value) {
        return false;
      }

      // For cluster filters, validate only via cluster data (top-level selectedOperation may be unset)
      if (filter.isCluster && filter.clusterData && filter.clusterData.length > 0) {
        return filter.clusterData.some((cluster) => {
          const clusterOp = cluster.selectedOperation?.value;
          if (
            clusterOp === DisableContent.isEmpty.value ||
            clusterOp === DisableContent.isNotEmpty.value
          ) {
            return true;
          }
          return !!cluster.selectedCluster?.value && hasFilterOperandValue(clusterOp, cluster.value);
        });
      }

      // For non-cluster filters, require top-level operation
      if (!filter.selectedOperation?.value) {
        return false;
      }

      // For isEmpty/isNotEmpty operations, value can be empty
      if (
        filter.selectedOperation.value === DisableContent.isEmpty.value ||
        filter.selectedOperation.value === DisableContent.isNotEmpty.value
      ) {
        return true;
      }

      // For non-cluster filters, check if value is not empty (support string or array e.g. queue names)
      if (Array.isArray(filter.value)) {
        return filter.value.length > 0;
      }
      return filter.value != null && filter.value !== '' && String(filter.value).trim().length > 0;
    });

    this.setFilterApplied(hasValidFilters);
  };

  clearCacheOnFilterChange = () => {
    // Clear chart data cache when filters change to ensure fresh data
    this.apiCacheService.clearEndpointCache('chart_data');
    // Clear overview data cache when filters change to ensure fresh data
    this.apiCacheService.clearEndpointCache('overview_data');
  };

  addNewCondition = () => {
    const emptyConditionIndex = this._tempFilterData$.value.findIndex((condition) => !condition.index);

    let tempCondition = {
      index: emptyConditionIndex > -1 ? 1 : this._tempFilterData$.value.length + 1,
      operator: emptyConditionIndex > -1 ? null : 'AND',
      selectedColumn: undefined,
      selectedOperation: undefined,
      value: '',
      isCluster: false,
      clusterData: [],
    };

    if (emptyConditionIndex > -1) {
      // If an empty condition is found, replace it with the new tempCondition
      this._tempFilterData$.value[emptyConditionIndex] = tempCondition;
      this._tempFilterData$.next([...this._tempFilterData$.value]);
    } else {
      if (this._tempFilterData$.value.length > 1) {
        tempCondition.operator = this._tempFilterData$.value[this._tempFilterData$.value.length - 1]?.operator ?? null;
      }

      // If no empty condition is found, add the new tempCondition
      this._tempFilterData$.next([...this._tempFilterData$.value, tempCondition]);
    }
    this.setSavedButtonEnabled(true);
    this.updateApplyButtonState();
  };

  updateCondition = (filterItemLocal: Types.FilterItemLocal, keepReference = false, _enableApplyButton = false) => {
    const conditionIndex = this._tempFilterData$.value.findIndex((condition) => condition.index === filterItemLocal.index);

    if (conditionIndex > -1) {
      const op = filterItemLocal.selectedOperation?.value;
      const value = serializeFilterOperandValue(op, filterItemLocal.value ?? '');
      const clusterData = (filterItemLocal?.clusterData || []).map((cluster) => {
        const clusterOp = cluster.selectedOperation?.value;
        return { ...cluster, value: serializeFilterOperandValue(clusterOp, cluster.value) };
      });
      let updatedCondition: Types.FilterCondition = {
        ...this._tempFilterData$.value[conditionIndex],
        operator: filterItemLocal.operator ? filterItemLocal.operator.label : null,
        selectedColumn: filterItemLocal.selectedColumn ? filterItemLocal.selectedColumn : undefined,
        selectedOperation: filterItemLocal.selectedOperation ? filterItemLocal.selectedOperation : undefined,
        value,
        isCluster: filterItemLocal.isCluster,
        clusterName: filterItemLocal.clusterName,
        clusterData,
      };

      // Update the condition in the array
      this._tempFilterData$.value[conditionIndex] = updatedCondition;

      if (!keepReference) {
        // Emit the updated array
        this._tempFilterData$.next([...this._tempFilterData$.value]);
      }

      this.setSavedButtonEnabled(true);
      this.updateApplyButtonState();
    } else {
      console.error('Condition not found for update.');
    }
  };

  removeCondition = (index: number) => {
    const conditionIndex = this._tempFilterData$.value.findIndex((condition) => condition.index === index);

    // If the condition is found, remove it from the array
    if (conditionIndex > -1) {
      const updatedConditions = this._tempFilterData$.value.filter((condition) => condition.index !== index);

      if (updatedConditions.length === 0) {
        this._tempFilterData$.next([{ ...InitialFilterData } as Types.FilterCondition]);
      } else {
        const reindexedConditions = updatedConditions.map((item, index) => ({
          ...item,
          index: index + 1,
        }));

        if (conditionIndex === 0) {
          reindexedConditions[0].operator = null;
        }
        this._tempFilterData$.next([...reindexedConditions]);
      }
      this.updateApplyButtonState();
    }
  };

  addEmptyCluster = (conditionIndex: number, selectedColumn?: Types.DropdownObjectType, clusterName?: string) => {
    let initClusterItem: Types.ClusterData = {
      index: 1,
      selectedCluster: undefined,
      selectedOperation: undefined,
      value: '',
    };

    let updatedCondition: Types.FilterCondition = {
      ...this._tempFilterData$.value[conditionIndex - 1],
      isCluster: true,
      clusterName: clusterName,
      selectedColumn: selectedColumn,
      clusterData: [initClusterItem],
    };

    this._tempFilterData$.value[conditionIndex - 1] = updatedCondition;
    this._tempFilterData$.next([...this._tempFilterData$.value]);
  };

  addCluster = (clusterIndex: number, conditionIndex: number) => {
    let clusterItem: Types.ClusterData = {
      index: clusterIndex + 1,
      selectedCluster: undefined,
      selectedOperation: { label: '', value: '' },
      value: '',
    };

    let updatedCondition: Types.FilterCondition = {
      ...this._tempFilterData$.value[conditionIndex - 1],
      clusterData: [...(this._tempFilterData$.value[conditionIndex - 1]?.clusterData ?? []), clusterItem],
    };

    this._tempFilterData$.value[conditionIndex - 1] = updatedCondition;
    this._tempFilterData$.next([...this._tempFilterData$.value]);
  };

  removeClusterItem = (clusterIndex: number, conditionIndex: number) => {
    const condition = this._tempFilterData$.value[conditionIndex - 1];

    if (condition?.clusterData) {
      // Remove the cluster item
      const updatedRootCauseData = condition.clusterData.filter((_, index) => index !== clusterIndex - 1);

      // Re-Index the remaining items
      const reindexedRootCauseData = updatedRootCauseData.map((item, index) => ({
        ...item,
        index: index + 1, // Reassign the index starting from 1
      }));

      const updatedCondition: Types.FilterCondition = {
        ...condition,
        clusterData: reindexedRootCauseData,
      };

      // Update the filter data
      this._tempFilterData$.value[conditionIndex - 1] = updatedCondition;
      this._tempFilterData$.next([...this._tempFilterData$.value]);
    }
  };

  changeAllOperators = (value: Types.DropdownObjectType) => {
    let updatedConditions = this._tempFilterData$.value.map((item, index) => ({
      ...item,
      operator: index >= 1 ? value.label.toUpperCase() : item.operator?.toUpperCase(),
    }));

    this._tempFilterData$.next([...updatedConditions]);
    this.updateApplyButtonState();
  };

  reIndexArrayAfterDragDrop = (data: Array<Types.FilterCondition>) => {
    const otherOperator = this._tempFilterData$.value.find((item) => item.operator);

    const reindexedConditions = data.map((item, index) => ({
      ...item,
      index: index + 1, // Reassign the index starting from 1
      operator: index === 0 ? null : (otherOperator?.operator ?? 'And'),
    }));
    // Update the observable with the new array
    this._tempFilterData$.next([...reindexedConditions]);
    this.updateApplyButtonState();
  };

  clearFilters = () => {
    this._tempFilterData$.next([
      {
        index: 1,
        operator: null,
        selectedColumn: undefined,
        selectedOperation: undefined,
        value: '',
        isCluster: false,
        clusterData: [],
      } as Types.FilterCondition,
    ]);
    this.updateApplyButtonState();
  };

  _checkCustomerEndpoint(): { column: boolean; value: boolean } {
    return this._tempFilterData$.value.some(
      (item) => item.selectedColumn?.value === 'CustomerEndpoint_Address' && item.value?.length > 0
    )
      ? { column: true, value: true }
      : { column: false, value: false };
  }

  cleanAndPrepareConditions() {
    const cleanedConditions = this._tempFilterData$.value.filter(
      (condition) => condition.value && condition.value.length > 0
    );

    if (cleanedConditions.length === 0) {
      this._tempFilterData$.next([{ ...InitialFilterData } as Types.FilterCondition]);
    } else {
      this._tempFilterData$.next(cleanedConditions);
      this.addNewCondition();
    }
  }

  createCustomerEndpointCondition(): Types.FilterCondition {
    let updatedCondition = {
      ...this._tempFilterData$.value[this._tempFilterData$.value.length - 1],
      selectedColumn: {
        label: 'Customer Phone Number',
        value: 'CustomerEndpoint_Address',
        type: 'string' as Types.ColumnTypes,
      },
      selectedOperation: {
        label: 'contains',
        value: 'contains',
      },
    };

    return updatedCondition;
  }

  handleCustomerEndpointAddress() {
    const { column } = this._checkCustomerEndpoint();

    if (!column) {
      this.cleanAndPrepareConditions();
      const updatedCondition = this.createCustomerEndpointCondition();

      this._tempFilterData$.value[this._tempFilterData$.value.length - 1] = updatedCondition;
      this._tempFilterData$.next([...this._tempFilterData$.value]);

      setTimeout(() => {
        this.sharedDataService.toggleFilter(true);
      }, 200);
    }
  }

  checkCustomerEndpointSelected() {
    let selected = this._checkCustomerEndpoint();
    this._customerEndpointAddressSelected.set(selected.column && selected.value);
  }
}
